/**
 * Chart helpers for Salon Analysis components
 */

// Month names in Polish
export const MONTH_NAMES = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
]

// Short month names
export const MONTH_NAMES_SHORT = [
  'Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze',
  'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'
]

// Operator colors
export const OPERATOR_COLORS = {
  orange: {
    primary: '#FF7900',
    secondary: '#FFB366',
    gradient: 'rgba(255, 121, 0, 0.8)',
    light: 'rgba(255, 121, 0, 0.2)'
  },
  plus: {
    primary: '#00A651',
    secondary: '#66CC99',
    gradient: 'rgba(0, 166, 81, 0.8)',
    light: 'rgba(0, 166, 81, 0.2)'
  },
  play: {
    primary: '#9333EA',
    secondary: '#C084FC',
    gradient: 'rgba(147, 51, 234, 0.8)',
    light: 'rgba(147, 51, 234, 0.2)'
  },
  tmobile: {
    primary: '#E20074',
    secondary: '#FF66B2',
    gradient: 'rgba(226, 0, 116, 0.8)',
    light: 'rgba(226, 0, 116, 0.2)'
  }
}

/**
 * Get operator color configuration
 */
export const getOperatorColors = (operator) => {
  return OPERATOR_COLORS[operator?.toLowerCase()] || OPERATOR_COLORS.orange
}

/**
 * Prepare TOP 20 chart data from salon data
 */
export const prepareTop20ChartData = (salonData, limit = 20) => {
  if (!salonData?.length) return []
  
  return salonData.slice(0, limit).map((salon, index) => ({
    name: `${salon.plant_code} - ${salon.city}`,
    fullName: salon.salon_name,
    plantCode: salon.plant_code,
    city: salon.city,
    sales: Number(salon.total_sales) || 0,
    index: index + 1
  }))
}

/**
 * Prepare Pareto chart data from salon data
 */
export const prepareParetoChartData = (salonData, limit = 100) => {
  if (!salonData?.length) return []
  
  const activeSalons = salonData.filter(s => s.total_sales > 0)
  const totalSales = activeSalons.reduce((sum, s) => sum + s.total_sales, 0)
  let cumulativeSales = 0
  
  return activeSalons.slice(0, Math.min(limit, activeSalons.length)).map((salon, index) => {
    cumulativeSales += salon.total_sales
    const cumulativePercentage = (cumulativeSales / totalSales) * 100
    
    return {
      index: index + 1,
      salon: salon.salon_name,
      plantCode: salon.plant_code,
      sales: salon.total_sales,
      cumulative: cumulativePercentage
    }
  })
}

/**
 * Calculate Pareto statistics (80/20 and 70/30 rules)
 * Handles two formats:
 * - Array format (Plus/Play): array of {salon_rank, cumulative_percent, salon_percent}
 * - Object format (Orange): {80_20: {...}, 70_30: {...}}
 */
export const calculateParetoStats = (paretoAnalysis) => {
  if (!paretoAnalysis) return { '80_20': null, '70_30': null }
  
  // Check if it's the Orange object format (has 80_20 key directly)
  if (paretoAnalysis['80_20'] || paretoAnalysis['70_30']) {
    return {
      '80_20': paretoAnalysis['80_20'] ? {
        top_salons_count: paretoAnalysis['80_20'].top_salons_count || 0,
        salon_percentage: paretoAnalysis['80_20'].salon_percentage || 0
      } : null,
      '70_30': paretoAnalysis['70_30'] ? {
        top_salons_count: paretoAnalysis['70_30'].top_salons_count || 0,
        salon_percentage: paretoAnalysis['70_30'].salon_percentage || 0
      } : null
    }
  }
  
  // Array format (Plus/Play) - calculate from array
  if (!paretoAnalysis?.length) return { '80_20': null, '70_30': null }
  
  // Find 80% threshold
  const salon80 = paretoAnalysis.find(item => item.cumulative_percent >= 80) || paretoAnalysis[paretoAnalysis.length - 1]
  const salon70 = paretoAnalysis.find(item => item.cumulative_percent >= 70) || paretoAnalysis[paretoAnalysis.length - 1]
  
  return {
    '80_20': {
      top_salons_count: salon80?.salon_rank || 0,
      salon_percentage: salon80?.salon_percent || 0
    },
    '70_30': {
      top_salons_count: salon70?.salon_rank || 0,
      salon_percentage: salon70?.salon_percent || 0
    }
  }
}

/**
 * Prepare heatmap data from salon data
 */
export const prepareHeatmapData = (salonData, limit = 50) => {
  if (!salonData?.length) return []
  
  // Add calculated fields for sorting
  const salonDataCopy = salonData.map(salon => {
    const monthlyData = salon.monthly_sales || {}
    const monthValues = Object.values(monthlyData).map(v => Number(v) || 0)
    const maxMonthlySales = Math.max(...monthValues, 0)
    const activeMonths = monthValues.filter(sales => sales > 0).length
    
    return {
      ...salon,
      max_monthly_sales: maxMonthlySales,
      active_months: activeMonths
    }
  })
  
  // Sort by: max monthly sales (desc), active months (desc), total sales (desc)
  salonDataCopy.sort((a, b) => {
    if (b.max_monthly_sales !== a.max_monthly_sales) {
      return b.max_monthly_sales - a.max_monthly_sales
    }
    if (b.active_months !== a.active_months) {
      return b.active_months - a.active_months
    }
    return b.total_sales - a.total_sales
  })
  
  const salonsWithSales = salonDataCopy.filter(s => s.total_sales > 0)
  const limitedSalons = salonsWithSales.slice(0, limit)
  
  return limitedSalons.map((salon, rowIndex) => {
    const monthlyData = {}
    for (let i = 1; i <= 12; i++) {
      monthlyData[`month_${i}`] = salon.monthly_sales?.[i.toString()] || 0
    }
    
    const name = salon.salon_name || `Salon ${salon.plant_code || 'N/A'}`
    const plantCode = salon.plant_code || 'N/A'
    const displayName = name.length > 20 
      ? `${name.substring(0, 20)}... (${plantCode})`
      : `${name} (${plantCode})`
    
    return {
      id: rowIndex,
      salon: displayName,
      fullSalonName: salon.salon_name,
      plantCode: salon.plant_code,
      totalSales: salon.total_sales,
      maxMonthlySales: salon.max_monthly_sales,
      activeMonths: salon.active_months,
      ...monthlyData
    }
  })
}

/**
 * Get heatmap cell color based on value and intensity
 */
export const getHeatmapCellColor = (value, maxValue, operatorColor) => {
  if (value <= 0) return 'rgba(240, 240, 240, 0.5)'
  
  const intensity = maxValue > 0 ? value / maxValue : 0
  const colors = getOperatorColors(operatorColor)
  
  // Interpolate between light and primary color
  return `rgba(${hexToRgb(colors.primary)}, ${0.2 + intensity * 0.8})`
}

/**
 * Convert hex color to RGB string
 */
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!result) return '0, 0, 0'
  return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
}

/**
 * Format number with locale
 */
export const formatNumber = (value) => {
  if (value === null || value === undefined) return '0'
  return Number(value).toLocaleString('pl-PL')
}

/**
 * Format percentage
 */
export const formatPercent = (value, decimals = 1) => {
  if (value === null || value === undefined) return '0%'
  return `${Number(value).toFixed(decimals)}%`
}
